import { auth } from '../auth/state.js';
import { saveFavoriteFaction } from '../auth/api.js';
import { rankFromPoints } from '../game/rank.js';
import { fetchFactions, type FactionDef } from '../lobby/api.js';
import { el } from './login.js';
import { renderCosmetics } from './cosmetics.js';

/**
 * Compact profile screen with three tabs:
 *   • Profil       — header card + rank widget + classic stats
 *   • Cosmétiques  — weapon loadout / unlocks (existing panel)
 *   • Royaume favori — kingdom picker auto-locked in ranked
 *
 * Active tab is remembered for the session (module-level so navigating
 * away and back keeps the same tab open).
 */
type ProfileTab = 'profile' | 'cosmetics' | 'favorite';
let activeProfileTab: ProfileTab = 'profile';

export function renderProfile(container: HTMLElement): void {
  container.innerHTML = '';
  container.classList.add('overlay-screen');

  const user = auth.user;
  if (!user) {
    container.appendChild(el('p', 'auth-error', 'Connexion requise.'));
    return;
  }

  const wrap = el('div', 'profile-wrap');

  // ── Compact header (avatar + name only — no email/dates inline) ──
  const header = el('div', 'profile-header');
  const avatar = el('div', 'profile-avatar', user.username[0]?.toUpperCase() ?? '?');
  const headerText = el('div', 'profile-header-text');
  const nameRow = document.createElement('h1');
  nameRow.className = 'profile-name';
  const nameIcon = document.createElement('img');
  nameIcon.src = '/sprites/iconchat_neutre.png';
  nameIcon.className = 'page-title-icon';
  nameIcon.alt = '';
  nameRow.appendChild(nameIcon);
  nameRow.appendChild(document.createTextNode(user.username));
  headerText.appendChild(nameRow);
  // Email + member-since collapsed to a single muted line under the name
  // so the header takes half the vertical space.
  const sub = document.createElement('p');
  sub.className = 'profile-subline';
  sub.textContent = `${user.email} · membre depuis le ${new Date(user.createdAt).toLocaleDateString('fr-FR')}`;
  headerText.appendChild(sub);
  header.append(avatar, headerText);
  wrap.appendChild(header);

  // ── Tab strip ────────────────────────────────────────────────────
  const tabs = el('div', 'profile-tabs');
  const tabBtns: Record<ProfileTab, HTMLButtonElement> = {
    profile:   makeTab('Profil',          'profile',   'iconchat_neutre'),
    cosmetics: makeTab('Cosmétiques',     'cosmetics', 'iconchat_lancier'),
    favorite:  makeTab('Royaume favori',  'favorite',  'iconchat_champion'),
  };
  tabs.append(tabBtns.profile, tabBtns.cosmetics, tabBtns.favorite);
  wrap.appendChild(tabs);

  // ── Tab body — only the active tab's panel is mounted ────────────
  const body = el('div', 'profile-tab-body');
  wrap.appendChild(body);
  container.appendChild(wrap);

  renderActiveTab();

  function makeTab(label: string, id: ProfileTab, icon: string): HTMLButtonElement {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'profile-tab' + (activeProfileTab === id ? ' is-active' : '');
    const ico = document.createElement('img');
    ico.src = `/sprites/${icon}.png`;
    ico.className = 'profile-tab-icon';
    ico.alt = '';
    b.appendChild(ico);
    b.appendChild(document.createTextNode(label));
    b.addEventListener('click', () => {
      if (activeProfileTab === id) return;
      activeProfileTab = id;
      for (const t of Object.keys(tabBtns) as ProfileTab[]) {
        tabBtns[t].classList.toggle('is-active', t === id);
      }
      renderActiveTab();
    });
    return b;
  }

  function renderActiveTab(): void {
    body.innerHTML = '';
    if (activeProfileTab === 'profile') {
      // Rank widget + classic stats — the "at a glance" tab.
      body.appendChild(renderRankCard(user!.rankedPoints, user!.rankedGamesPlayed, user!.rankedGamesWon));
      body.appendChild(renderStatsGrid(user!));
    } else if (activeProfileTab === 'cosmetics') {
      const cosmHost = el('div', 'profile-cosmetics-host');
      body.appendChild(cosmHost);
      void renderCosmetics(cosmHost);
    } else if (activeProfileTab === 'favorite') {
      const favHost = el('div', 'profile-favorite-host');
      body.appendChild(favHost);
      void renderFavoriteFactionPicker(favHost);
    }
  }
}

function renderStatsGrid(user: NonNullable<typeof auth.user>): HTMLElement {
  const wrap = el('div', 'profile-stats');
  const winRate =
    user.gamesPlayed > 0
      ? `${Math.round((user.gamesWon / user.gamesPlayed) * 100)}%`
      : '—';
  const losses = Math.max(0, user.gamesPlayed - user.gamesWon);
  wrap.appendChild(statCard('Parties jouées', user.gamesPlayed.toString(), 'iconchat_crier'));
  wrap.appendChild(statCard('Parties gagnées', user.gamesWon.toString(), 'iconchat_valide'));
  wrap.appendChild(statCard('Défaites', losses.toString(), 'iconchat_perdu'));
  wrap.appendChild(statCard('Taux de victoire', winRate, 'iconchat_choquer'));
  wrap.appendChild(statCard('Moustaches d\'or', `${user.shinies ?? 0}`, 'iconchat_neutre'));
  return wrap;
}

function statCard(label: string, value: string, icon?: string): HTMLElement {
  const c = el('div', 'stat-card');
  if (icon) {
    const img = document.createElement('img');
    img.src = `/sprites/${icon}.png`;
    img.className = 'stat-icon';
    img.alt = '';
    c.appendChild(img);
  }
  c.appendChild(el('div', 'stat-value', value));
  c.appendChild(el('div', 'stat-label', label));
  return c;
}

/** Big rank widget — tier icon + name, current points, progress bar
 *  towards the next tier, plus the ranked W/L counts. */
function renderRankCard(points: number, played: number, won: number): HTMLElement {
  const r = rankFromPoints(points);
  const card = el('div', 'profile-rank-card');

  const left = el('div', 'profile-rank-left');
  const frame = el('div', 'profile-rank-frame rank-frame');
  frame.style.setProperty('--rank-tint', r.tier.tintColor);
  frame.style.setProperty('--rank-tint-deep', r.tier.tintColorDeep);
  const icon = document.createElement('img');
  icon.src = r.tier.icon;
  icon.className = 'profile-rank-icon';
  icon.alt = '';
  frame.appendChild(icon);
  left.appendChild(frame);
  card.appendChild(left);

  const main = el('div', 'profile-rank-main');
  const titleRow = el('div', 'profile-rank-title-row');
  titleRow.appendChild(el('span', 'profile-rank-tier-label', `Tier ${r.tier.tier} · ${r.tier.material}`));
  titleRow.appendChild(el('span', 'profile-rank-tier-name', r.tier.name));
  main.appendChild(titleRow);

  const ptsLine = el('div', 'profile-rank-pts');
  ptsLine.appendChild(el('span', 'profile-rank-pts-value', points.toString()));
  ptsLine.appendChild(el('span', 'profile-rank-pts-label', ' pts de patrouille'));
  main.appendChild(ptsLine);

  if (r.tierBandWidth > 0 && Number.isFinite(r.pointsToNext)) {
    const barWrap = el('div', 'profile-rank-bar-wrap');
    const bar = el('div', 'profile-rank-bar');
    const fill = el('div', 'profile-rank-bar-fill');
    const pct = Math.max(0, Math.min(100, Math.round(100 * r.pointsInTier / r.tierBandWidth)));
    fill.style.width = `${pct}%`;
    bar.appendChild(fill);
    barWrap.appendChild(bar);
    barWrap.appendChild(
      el('span', 'profile-rank-bar-hint', `${r.pointsToNext} pts avant le prochain rang`),
    );
    main.appendChild(barWrap);
  } else {
    main.appendChild(el('div', 'profile-rank-bar-hint', '✨ Rang maximum atteint, NIA suprême !'));
  }
  card.appendChild(main);

  const right = el('div', 'profile-rank-right');
  right.appendChild(el('div', 'profile-rank-stat-value', played.toString()));
  right.appendChild(el('div', 'profile-rank-stat-label', 'Patrouilles classées'));
  right.appendChild(el('div', 'profile-rank-stat-value', won.toString()));
  right.appendChild(el('div', 'profile-rank-stat-label', 'Victoires'));
  card.appendChild(right);

  return card;
}

/** Faction grid — pick one as your default for ranked queueing. */
async function renderFavoriteFactionPicker(host: HTMLElement): Promise<void> {
  host.innerHTML = '';
  const loading = el('div', 'cosm-loading', '🐾 Chargement des royaumes…');
  host.appendChild(loading);
  let factions: FactionDef[] = [];
  try {
    factions = await fetchFactions();
  } catch {
    loading.textContent = 'Impossible de charger les royaumes.';
    return;
  }
  // Bail out if a concurrent render already swapped the host content
  // (e.g. the user clicked a tab while we were awaiting).
  if (!loading.isConnected) return;
  loading.remove();
  const card = el('div', 'profile-favorite-card');

  card.appendChild(
    el(
      'p',
      'profile-favorite-hint',
      'Le royaume choisi est auto-équipé quand tu lances une partie classée. Clique sur ton favori — la sélection est sauvée tout de suite.',
    ),
  );

  const grid = el('div', 'profile-favorite-grid');
  for (const f of factions) {
    const cell = document.createElement('button');
    cell.type = 'button';
    cell.className = 'profile-favorite-cell';
    cell.style.setProperty('--faction-color', f.color);
    if (auth.user?.favoriteFactionId === f.id) cell.classList.add('is-active');
    cell.title = f.name + (f.lore ? ' — ' + f.lore : '');
    const img = document.createElement('img');
    img.src = `/sprites/${f.id}_army.png`;
    img.alt = f.name;
    cell.appendChild(img);
    cell.appendChild(el('span', 'profile-favorite-cell-name', f.name));
    cell.addEventListener('click', () => {
      void selectFavorite(f.id);
    });
    grid.appendChild(cell);
  }
  card.appendChild(grid);
  host.appendChild(card);
}

async function selectFavorite(factionId: string): Promise<void> {
  if (!auth.user) return;
  try {
    const r = await saveFavoriteFaction(factionId);
    // Mutating auth.user triggers the auth subscriber, which in turn
    // re-runs the router → renderProfile → which re-mounts the active
    // tab freshly. We MUST NOT also manually re-render here: both calls
    // would race on the same host element and append two
    // `.profile-favorite-card` nodes — the duplicate section bug.
    auth.user = { ...auth.user, favoriteFactionId: r.favoriteFactionId };
  } catch (err) {
    console.error('save favorite failed', err);
  }
}
